import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Calculator, Shield, Target, GitCompareArrows, Scale, Layers, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { PageHero } from "@/components/PageHero";
import { Disclaimer } from "@/components/Shared";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

function Field({ label, value, onChange, testid, suffix }) {
  return (
    <label className="block">
      <span className="text-sm text-slate-600">{label}</span>
      <div className="relative mt-1.5">
        <input
          type="number" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
          className="w-full h-11 rounded-md border border-border px-3.5 text-sm text-navy outline-none focus:ring-2 focus:ring-gold"
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">{suffix}</span>}
      </div>
    </label>
  );
}

function ResultRow({ label, value, highlight }) {
  return (
    <div className={`flex items-center justify-between py-2.5 px-4 rounded-md ${highlight ? "bg-navy text-white" : "bg-subtle"}`} data-testid={`result-${label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
      <span className={`text-sm ${highlight ? "text-slate-300" : "text-slate-500"}`}>{label}</span>
      <span className={`font-display font-bold ${highlight ? "text-gold" : "text-navy"}`}>{value}</span>
    </div>
  );
}

const num = (v) => (v === "" || v == null || isNaN(parseFloat(v)) ? null : parseFloat(v));
const fmt = (v) => (v == null ? "—" : v.toLocaleString("en-IN", { maximumFractionDigits: 2 }));

// Verified against the OI_Analysis workbook (NIFTY/BANKNIFTY/SENSEX Day/Week/Month tables).
function computeCPR(H, L, C) {
  if (H == null || L == null || C == null) return null;
  const P = (H + L + C) / 3;
  const bc = (H + L) / 2;
  const tc = 2 * P - bc;
  const TC = Math.max(tc, bc), BC = Math.min(tc, bc);
  const widthPts = Math.abs(tc - bc);
  const widthPct = P ? Math.round((widthPts / P) * 10000) / 100 : 0;
  const size = widthPct < 0.11 ? "Narrow" : widthPct < 0.21 ? "Medium" : "Wide";
  const structure = size === "Narrow" ? "Trending (up/down)" : size === "Medium" ? "Balanced / transitional" : "Sideways / range-bound";
  const range = H - L;
  const trad = { R1: 2 * P - L, S1: 2 * P - H, R2: P + range, S2: P - range };
  for (let k = 3; k <= 6; k++) { trad["R" + k] = H + (k - 1) * (P - L); trad["S" + k] = L - (k - 1) * (H - P); }
  const H3 = C + range * 1.1 / 4, H4 = C + range * 1.1 / 2, L3 = C - range * 1.1 / 4, L4 = C - range * 1.1 / 2;
  const cam = { H3, H4, L3, L4, H5: H4 + 1.168 * (H4 - H3), L5: L4 - 1.168 * (L3 - L4), H6: L ? C * (H / L) : null, L6: H ? C * (L / H) : null };
  return { TC, P, BC, widthPts, widthPct, size, structure, trad, cam };
}

const IDX = ["NIFTY", "BANK NIFTY", "SENSEX"];
const PERIODS = [["day", "Day"], ["week", "Week"], ["month", "Month"]];
const blank = () => ({ day: { h: "", l: "", c: "" }, week: { h: "", l: "", c: "" }, month: { h: "", l: "", c: "" } });

const MATRIX_ROWS = [
  { sec: "Central Pivot Range" },
  { label: "Top Central (TC)", get: (r) => r.TC, tone: "res" },
  { label: "Pivot", get: (r) => r.P, pivot: true },
  { label: "Bottom Central (BC)", get: (r) => r.BC, tone: "sup" },
  { label: "CPR Width (pts)", get: (r) => r.widthPts },
  { label: "CPR Width %", get: (r) => r.widthPct, pct: true },
  { label: "CPR Size", get: (r) => r.size, text: true },
  { label: "Market Structure", get: (r) => r.structure, text: true },
  { sec: "Traditional Pivots" },
  ...["R6", "R5", "R4", "R3", "R2", "R1"].map((k) => ({ label: k, get: (r) => r.trad[k], tone: "res" })),
  ...["S1", "S2", "S3", "S4", "S5", "S6"].map((k) => ({ label: k, get: (r) => r.trad[k], tone: "sup" })),
  { sec: "Camarilla Pivots" },
  ...["H6", "H5", "H4", "H3"].map((k) => ({ label: k, get: (r) => r.cam[k], tone: "res" })),
  ...["L3", "L4", "L5", "L6"].map((k) => ({ label: k, get: (r) => r.cam[k], tone: "sup" })),
];

function cellText(row, res) {
  if (!res) return "—";
  const v = row.get(res);
  if (v == null) return "—";
  if (row.text) return v;
  if (row.pct) return `${v.toFixed(2)}%`;
  return fmt(v);
}

function CopyButton({ text, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); } catch { /* noop */ }
      ta.remove();
    }
    setCopied(true);
    toast.success("Levels copied to clipboard");
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <button onClick={copy} data-testid={testid} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-navy text-white text-sm font-medium hover:bg-navy-light transition-colors">
      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? "Copied" : "Copy levels"}
    </button>
  );
}

function buildCPRText(idx, res) {
  const lines = [`${idx} — CPR Levels (KRV Prime)`];
  PERIODS.forEach(([k, lbl]) => {
    const r = res[k];
    if (!r) return;
    lines.push(`\n${lbl}:`);
    lines.push(`TC ${fmt(r.TC)} | Pivot ${fmt(r.P)} | BC ${fmt(r.BC)}  (${r.widthPct.toFixed(2)}%, ${r.size})`);
    lines.push(`R: ${fmt(r.trad.R1)} / ${fmt(r.trad.R2)} / ${fmt(r.trad.R3)}`);
    lines.push(`S: ${fmt(r.trad.S1)} / ${fmt(r.trad.S2)} / ${fmt(r.trad.S3)}`);
    lines.push(`Camarilla: H4 ${fmt(r.cam.H4)}  H3 ${fmt(r.cam.H3)} | L3 ${fmt(r.cam.L3)}  L4 ${fmt(r.cam.L4)}`);
  });
  lines.push("\nEducational only. Not investment advice.");
  return lines.join("\n");
}

function buildOIText(idx, res) {
  return [
    `${idx} — OI Support/Resistance (KRV Prime)`,
    `Resistance (max Call OI): ${fmt(res.r1.s)}${res.r2 ? ` | next ${fmt(res.r2.s)}` : ""}`,
    `Support (max Put OI): ${fmt(res.s1.s)}${res.s2 ? ` | next ${fmt(res.s2.s)}` : ""}`,
    `Total Call OI ${fmt(res.totCE)} | Total Put OI ${fmt(res.totPE)}`,
    `PCR ${res.pcr == null ? "—" : res.pcr.toFixed(2)} · Bias: ${res.bias}`,
    "Educational only. Not investment advice.",
  ].join("\n");
}

function CPRMatrix() {
  const [idx, setIdx] = useState("NIFTY");
  const [data, setData] = useState(() => Object.fromEntries(IDX.map((i) => [i, blank()])));
  const cur = data[idx];
  const setVal = (period, field, v) =>
    setData((d) => ({ ...d, [idx]: { ...d[idx], [period]: { ...d[idx][period], [field]: v } } }));
  const res = Object.fromEntries(PERIODS.map(([k]) => [k, computeCPR(num(cur[k].h), num(cur[k].l), num(cur[k].c))]));
  const any = PERIODS.some(([k]) => res[k]);
  const toneCls = { res: "text-emerald-700", sup: "text-rose-600" };

  return (
    <div className="space-y-6" data-testid="cpr-matrix">
      <div className="flex flex-wrap gap-2">
        {IDX.map((i) => (
          <button
            key={i}
            data-testid={`cpr-idx-${i.toLowerCase().replace(/\s/g, "-")}`}
            onClick={() => setIdx(i)}
            className={`h-9 px-4 rounded-md text-sm font-medium transition-colors ${idx === i ? "bg-navy text-white" : "bg-subtle text-slate-600 hover:bg-slate-200"}`}
          >
            {i}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              <th className="text-left p-2 text-xs uppercase tracking-wider text-slate-400 font-semibold">{idx} — Input</th>
              {PERIODS.map(([k, lbl]) => (
                <th key={k} className="p-2 text-xs uppercase tracking-wider text-navy font-semibold text-center">{lbl}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[["h", "High"], ["l", "Low"], ["c", "Close"]].map(([f, lbl]) => (
              <tr key={f}>
                <td className="p-2 text-slate-600">{lbl}</td>
                {PERIODS.map(([k]) => (
                  <td key={k} className="p-1.5">
                    <input
                      type="number"
                      value={cur[k][f]}
                      onChange={(e) => setVal(k, f, e.target.value)}
                      data-testid={`cpr-${k}-${f}`}
                      className="w-full h-10 rounded-md border border-border px-2 text-sm text-navy text-center outline-none focus:ring-2 focus:ring-gold"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {any && <div className="flex justify-end"><CopyButton text={buildCPRText(idx, res)} testid="cpr-copy-levels" /></div>}
      {any ? (
        <div className="overflow-x-auto border border-border rounded-lg">
          <table className="w-full border-collapse text-sm" data-testid="cpr-matrix-results">
            <thead>
              <tr className="bg-navy text-white">
                <th className="text-left p-2.5 font-semibold">Level</th>
                {PERIODS.map(([k, lbl]) => <th key={k} className="p-2.5 text-center font-semibold">{lbl}</th>)}
              </tr>
            </thead>
            <tbody>
              {MATRIX_ROWS.map((row, i) =>
                row.sec ? (
                  <tr key={`s-${i}`} className="bg-subtle">
                    <td colSpan={4} className="px-2.5 py-1.5 text-[11px] uppercase tracking-wider font-bold text-gold">{row.sec}</td>
                  </tr>
                ) : (
                  <tr key={row.label} className={`border-t border-border ${row.pivot ? "bg-gold/5" : ""}`}>
                    <td className={`px-2.5 py-1.5 ${row.pivot ? "font-bold text-navy" : "text-slate-600"} ${row.tone ? toneCls[row.tone] : ""}`}>{row.label}</td>
                    {PERIODS.map(([k]) => (
                      <td key={k} className={`px-2.5 py-1.5 text-center font-medium ${row.pivot ? "text-navy font-bold" : "text-slate-700"}`} data-testid={`cpr-out-${row.label.replace(/[^a-zA-Z0-9]/g, "")}-${k}`}>
                        {cellText(row, res[k])}
                      </td>
                    ))}
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-slate-400 p-4 bg-subtle rounded-md">Enter High, Low and Close for Day / Week / Month to compute the full CPR, pivot and Camarilla matrix.</p>
      )}
      <p className="text-xs text-slate-400">CPR size bands (Narrow &lt;0.11%, Medium 0.11–0.20%, Wide &gt;0.20%) and market-structure mapping match the KRV OI analysis methodology.</p>
    </div>
  );
}

function CPRRelationship() {
  const [t, setT] = useState({ h: "", l: "", c: "" });
  const [y, setY] = useState({ h: "", l: "", c: "" });
  const rt = computeCPR(num(t.h), num(t.l), num(t.c));
  const ry = computeCPR(num(y.h), num(y.l), num(y.c));
  let verdict = null, bias = null;
  if (rt && ry) {
    if (rt.BC > ry.TC) { verdict = "Higher Value CPR (Ascending)"; bias = "Bullish — trend continuation likely"; }
    else if (rt.TC < ry.BC) { verdict = "Lower Value CPR (Descending)"; bias = "Bearish — downtrend continuation likely"; }
    else if (rt.TC <= ry.TC && rt.BC >= ry.BC) { verdict = "Inside Value CPR"; bias = "Low volatility / breakout pending"; }
    else if (rt.TC >= ry.TC && rt.BC <= ry.BC) { verdict = "Outside Value CPR"; bias = "High volatility / trend day possible"; }
    else { verdict = "Overlapping CPR"; bias = "Sideways / range-bound bias"; }
  }
  return (
    <div className="space-y-8" data-testid="cpr-relationship">
      <div className="grid sm:grid-cols-2 gap-8">
        <div className="space-y-3">
          <p className="text-sm font-semibold text-navy">Yesterday (prior period)</p>
          <Field label="High" value={y.h} onChange={(v) => setY({ ...y, h: v })} testid="rel-y-h" />
          <Field label="Low" value={y.l} onChange={(v) => setY({ ...y, l: v })} testid="rel-y-l" />
          <Field label="Close" value={y.c} onChange={(v) => setY({ ...y, c: v })} testid="rel-y-c" />
        </div>
        <div className="space-y-3">
          <p className="text-sm font-semibold text-navy">Today (current period)</p>
          <Field label="High" value={t.h} onChange={(v) => setT({ ...t, h: v })} testid="rel-t-h" />
          <Field label="Low" value={t.l} onChange={(v) => setT({ ...t, l: v })} testid="rel-t-l" />
          <Field label="Close" value={t.c} onChange={(v) => setT({ ...t, c: v })} testid="rel-t-c" />
        </div>
      </div>
      {verdict ? (
        <div className="rounded-lg border border-gold/40 bg-gold/5 p-6" data-testid="cpr-rel-result">
          <p className="eyebrow text-gold">CPR Relationship</p>
          <p className="mt-2 font-display text-2xl font-bold text-navy">{verdict}</p>
          <p className="mt-1 text-sm text-slate-600">{bias}</p>
          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <ResultRow label="Today TC / BC" value={`${fmt(rt.TC)} / ${fmt(rt.BC)}`} />
            <ResultRow label="Yesterday TC / BC" value={`${fmt(ry.TC)} / ${fmt(ry.BC)}`} />
          </div>
        </div>
      ) : (
        <p className="text-sm text-slate-400 p-4 bg-subtle rounded-md">Enter both days' High/Low/Close to classify the CPR relationship.</p>
      )}
    </div>
  );
}

function PCRReading() {
  const [pcr, setPcr] = useState("");
  const p = num(pcr);
  let label = null, tone = "";
  if (p != null) {
    if (p >= 1.5) { label = "Extremely high PCR — overbought, reversal risk"; tone = "text-amber-600"; }
    else if (p >= 1.0) { label = "Bullish — puts outweigh calls"; tone = "text-emerald-700"; }
    else if (p >= 0.7) { label = "Neutral to mildly bullish"; tone = "text-navy"; }
    else if (p >= 0.4) { label = "Bearish — calls outweigh puts"; tone = "text-rose-600"; }
    else { label = "Extremely low PCR — oversold, bounce risk"; tone = "text-amber-600"; }
  }
  return (
    <div className="grid md:grid-cols-2 gap-8" data-testid="pcr-reading">
      <div className="space-y-4">
        <Field label="Put-Call Ratio (PCR)" value={pcr} onChange={setPcr} testid="pcr-input" />
        <p className="text-xs text-slate-400">Bullish above ~0.7 / 1.0; bearish below 0.7 toward 0.5. Extremes (≥1.5 or ≤0.4) can signal exhaustion.</p>
      </div>
      <div className="space-y-2">
        {label ? (
          <div className="rounded-lg border border-border p-6" data-testid="pcr-result">
            <p className="eyebrow text-gold">Interpretation</p>
            <p className={`mt-2 font-display text-xl font-bold ${tone}`}>{label}</p>
            <p className="mt-1 text-sm text-slate-500">PCR = {p.toFixed(2)}</p>
          </div>
        ) : <p className="text-sm text-slate-400 p-4 bg-subtle rounded-md">Enter a PCR value to see the reading.</p>}
      </div>
    </div>
  );
}

function PositionSize() {
  const [capital, setCapital] = useState("");
  const [riskPct, setRiskPct] = useState("1");
  const [entry, setEntry] = useState("");
  const [stop, setStop] = useState("");
  const cap = num(capital), rp = num(riskPct), en = num(entry), sl = num(stop);
  const ready = cap != null && rp != null && en != null && sl != null && en !== sl;
  const riskAmt = ready ? cap * (rp / 100) : null;
  const perUnit = ready ? Math.abs(en - sl) : null;
  const qty = ready ? Math.floor(riskAmt / perUnit) : null;
  return (
    <div className="grid md:grid-cols-2 gap-8">
      <div className="space-y-4">
        <Field label="Account Capital" value={capital} onChange={setCapital} testid="ps-capital" suffix="₹" />
        <Field label="Risk per Trade" value={riskPct} onChange={setRiskPct} testid="ps-risk" suffix="%" />
        <Field label="Entry Price" value={entry} onChange={setEntry} testid="ps-entry" />
        <Field label="Stop-Loss Price" value={stop} onChange={setStop} testid="ps-stop" />
      </div>
      <div className="space-y-2">
        <ResultRow label="Risk Amount" value={`₹ ${fmt(riskAmt)}`} />
        <ResultRow label="Risk per Unit" value={fmt(perUnit)} />
        <ResultRow label="Position Size (Qty)" value={qty == null ? "—" : fmt(qty)} highlight />
      </div>
    </div>
  );
}

function RiskReward() {
  const [entry, setEntry] = useState("");
  const [stop, setStop] = useState("");
  const [target, setTarget] = useState("");
  const en = num(entry), sl = num(stop), tg = num(target);
  const ready = en != null && sl != null && tg != null && en !== sl;
  const risk = ready ? Math.abs(en - sl) : null;
  const reward = ready ? Math.abs(tg - en) : null;
  const rr = ready && risk ? reward / risk : null;
  return (
    <div className="grid md:grid-cols-2 gap-8">
      <div className="space-y-4">
        <Field label="Entry Price" value={entry} onChange={setEntry} testid="rr-entry" />
        <Field label="Stop-Loss" value={stop} onChange={setStop} testid="rr-stop" />
        <Field label="Target" value={target} onChange={setTarget} testid="rr-target" />
      </div>
      <div className="space-y-2">
        <ResultRow label="Risk (points)" value={fmt(risk)} />
        <ResultRow label="Reward (points)" value={fmt(reward)} />
        <ResultRow label="Risk : Reward" value={rr == null ? "—" : `1 : ${rr.toFixed(2)}`} highlight />
      </div>
    </div>
  );
}

function OISupportResistance() {
  const [idx, setIdx] = useState("NIFTY");
  const [rows, setRows] = useState(() => Array.from({ length: 6 }, () => ({ s: "", ce: "", pe: "" })));
  const setCell = (i, k, v) => setRows((r) => r.map((row, x) => (x === i ? { ...row, [k]: v } : row)));
  const addRow = () => setRows((r) => [...r, { s: "", ce: "", pe: "" }]);
  const removeRow = (i) => setRows((r) => (r.length > 1 ? r.filter((_, x) => x !== i) : r));

  const valid = rows.map((r) => ({ s: num(r.s), ce: num(r.ce) || 0, pe: num(r.pe) || 0 })).filter((r) => r.s != null);
  let res = null;
  if (valid.length) {
    const byCE = [...valid].sort((a, b) => b.ce - a.ce);
    const byPE = [...valid].sort((a, b) => b.pe - a.pe);
    const totCE = valid.reduce((s, r) => s + r.ce, 0);
    const totPE = valid.reduce((s, r) => s + r.pe, 0);
    const pcr = totCE ? totPE / totCE : null;
    let bias = "—";
    if (pcr != null) bias = pcr >= 1.0 ? "Bullish (put writers dominant)" : pcr >= 0.7 ? "Neutral" : "Bearish (call writers dominant)";
    res = {
      r1: byCE[0], r2: byCE[1], s1: byPE[0], s2: byPE[1],
      totCE, totPE, pcr, bias,
    };
  }

  return (
    <div className="space-y-6" data-testid="oi-sr">
      <div className="flex flex-wrap gap-2">
        {IDX.map((i) => (
          <button key={i} onClick={() => setIdx(i)} data-testid={`oisr-idx-${i.toLowerCase().replace(/\s/g, "-")}`}
            className={`h-9 px-4 rounded-md text-sm font-medium transition-colors ${idx === i ? "bg-navy text-white" : "bg-subtle text-slate-600 hover:bg-slate-200"}`}>{i}</button>
        ))}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-slate-400">
              <th className="text-left p-2 font-semibold">Strike</th>
              <th className="p-2 font-semibold text-emerald-700">Call OI</th>
              <th className="p-2 font-semibold text-rose-600">Put OI</th>
              <th className="p-2 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {["s", "ce", "pe"].map((k) => (
                  <td key={k} className="p-1.5">
                    <input type="number" value={row[k]} onChange={(e) => setCell(i, k, e.target.value)} data-testid={`oisr-${k}-${i}`}
                      className="w-full h-10 rounded-md border border-border px-2 text-sm text-navy text-center outline-none focus:ring-2 focus:ring-gold" />
                  </td>
                ))}
                <td className="p-1.5 text-center">
                  <button onClick={() => removeRow(i)} data-testid={`oisr-remove-${i}`} className="text-slate-300 hover:text-rose-500 text-lg leading-none">×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button onClick={addRow} data-testid="oisr-add-row" className="mt-2 text-sm font-medium text-navy hover:text-gold transition-colors">+ Add strike</button>
      </div>

      {res && <div className="flex justify-end"><CopyButton text={buildOIText(idx, res)} testid="oisr-copy-levels" /></div>}
      {res ? (
        <div className="space-y-3" data-testid="oisr-results">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-4">
              <p className="eyebrow text-rose-600">Resistance (max Call OI)</p>
              <p className="mt-1 font-display text-2xl font-bold text-navy" data-testid="oisr-resistance">{fmt(res.r1.s)}</p>
              <p className="text-xs text-slate-500">Call OI {fmt(res.r1.ce)}{res.r2 ? ` · next ${fmt(res.r2.s)}` : ""}</p>
            </div>
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4">
              <p className="eyebrow text-emerald-700">Support (max Put OI)</p>
              <p className="mt-1 font-display text-2xl font-bold text-navy" data-testid="oisr-support">{fmt(res.s1.s)}</p>
              <p className="text-xs text-slate-500">Put OI {fmt(res.s1.pe)}{res.s2 ? ` · next ${fmt(res.s2.s)}` : ""}</p>
            </div>
          </div>
          <ResultRow label="Total Call OI" value={fmt(res.totCE)} />
          <ResultRow label="Total Put OI" value={fmt(res.totPE)} />
          <ResultRow label="PCR (Put/Call OI)" value={res.pcr == null ? "—" : res.pcr.toFixed(2)} highlight />
          <p className="text-sm text-slate-600 px-1">Bias: <span className="font-semibold text-navy">{res.bias}</span></p>
        </div>
      ) : (
        <p className="text-sm text-slate-400 p-4 bg-subtle rounded-md">Enter option strikes with their Call OI and Put OI. The tool marks the max-Call-OI strike as resistance and the max-Put-OI strike as support.</p>
      )}
      <p className="text-xs text-slate-400">Max Call-OI strike = key resistance (CE writers); max Put-OI strike = key support (PE writers). Educational aid, not investment advice.</p>
    </div>
  );
}

const TOOLS = [
  { id: "cpr-matrix", label: "CPR Matrix", icon: Calculator, node: <CPRMatrix /> },
  { id: "oi-sr", label: "OI S/R", icon: Layers, node: <OISupportResistance /> },
  { id: "cpr-rel", label: "CPR Relationship", icon: GitCompareArrows, node: <CPRRelationship /> },
  { id: "pcr", label: "PCR Reading", icon: Scale, node: <PCRReading /> },
  { id: "position", label: "Position Size", icon: Shield, node: <PositionSize /> },
  { id: "rr", label: "Risk / Reward", icon: Target, node: <RiskReward /> },
];

export default function Tools() {
  const [params] = useSearchParams();
  const ids = TOOLS.map((t) => t.id);
  const requested = params.get("tab");
  const initial = ids.includes(requested) ? requested : "cpr-matrix";
  return (
    <div data-testid="tools-page">
      <PageHero
        eyebrow="Educational & Analytical Tools"
        title="Calculators"
        subtitle="A full CPR matrix (Day/Week/Month per index) plus CPR relationship, PCR, position sizing and risk/reward calculators. Each is an educational/calculation aid — not personalised investment advice."
        crumbs={[{ label: "Tools" }, { label: "Calculators" }]}
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <Tabs defaultValue={initial} data-testid="tools-tabs">
          <TabsList className="flex flex-wrap h-auto bg-subtle p-1 gap-1">
            {TOOLS.map((t) => (
              <TabsTrigger key={t.id} value={t.id} data-testid={`tool-tab-${t.id}`} className="data-[state=active]:bg-navy data-[state=active]:text-white gap-1.5">
                <t.icon className="h-3.5 w-3.5" />{t.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {TOOLS.map((t) => (
            <TabsContent key={t.id} value={t.id} className="mt-8 bg-white border border-border rounded-lg p-8">
              <h2 className="font-display text-2xl font-bold text-navy mb-6">{t.label}</h2>
              {t.node}
            </TabsContent>
          ))}
        </Tabs>
        <div className="mt-10"><Disclaimer /></div>
      </div>
    </div>
  );
}
